"use client";

import { Gift, ArrowRight, Sparkles } from "lucide-react";
import { useRouter } from "next/navigation";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useLuckyGiftDynamics } from "../../hooks/useLuckyGiftDynamics";
import ActiveGifts from "./ActiveGifts";
import GiftDynamicsScroll from "./GiftDynamicsScroll";

interface LuckyGiftBannerProps {
	language: "zh" | "en";
}

export default function LuckyGiftBanner({ language }: LuckyGiftBannerProps) {
	const { activeGifts, dynamics, totalGifts, isLoading } = useLuckyGiftDynamics();
	const router = useRouter();

	const translations = {
		zh: {
			title: "奥丁红包",
			plaza: "进入红包",
		},
		en: {
			title: "Odin Gift",
			plaza: "Go to Gift",
		},
	};

	const t = translations[language];

	const handleEnterPlaza = () => {
		router.push("/plaza/lucky-gift");
	};

	return (
		<Card className="rounded-2xl bg-light-surface dark:bg-dark-surface border-light-border dark:border-dark-border shadow-md overflow-hidden relative group">
			{/* 背景装饰动效 - 调淡 */}
			<div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-red-500/5 rounded-full blur-2xl group-hover:bg-red-500/10 transition-all duration-700" />
			<div className="absolute bottom-0 left-0 -mb-8 -ml-8 w-32 h-32 bg-orange-500/5 rounded-full blur-3xl group-hover:bg-orange-500/10 transition-all duration-700" />

			<CardContent className="p-3 relative z-10">
				{/* 标题栏 */}
				<div className="flex items-center justify-between mb-3 px-1">
					<h2 className="text-base font-bold text-light-text dark:text-dark-text flex items-center gap-2">
						<div className="bg-red-500 p-1.5 rounded-lg">
							<Gift className="w-3.5 h-3.5 text-white" />
						</div>
						{t.title}
						{activeGifts.length > 0 && (
							<span className="flex h-1.5 w-1.5 relative">
								<span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
								<span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span>
							</span>
						)}
					</h2>
					<Button
						variant="ghost"
						size="sm"
						onClick={handleEnterPlaza}
						className="h-7 px-2 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 cursor-pointer"
					>
						<span className="text-xs">{t.plaza}</span>
						<ArrowRight className="w-3.5 h-3.5 ml-1" />
					</Button>
				</div>

				{/* 第一部分：置顶红包 */}
				<div className="mb-4">
					<ActiveGifts 
						gifts={activeGifts} 
						totalCount={totalGifts} 
						language={language} 
					/>
				</div>

				{/* 分割线 - 使用更中性的颜色 */}
				<div className="relative h-px w-full bg-gradient-to-r from-transparent via-light-border dark:via-dark-border to-transparent mb-4">
					<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-light-surface dark:bg-dark-surface px-2 text-[9px] text-gray-400 dark:text-gray-500 font-bold tracking-wider flex items-center gap-1">
						<Sparkles className="w-2.5 h-2.5 opacity-50" />
						LUCKY FEED
					</div>
				</div>

				{/* 第二部分：动态记录 */}
				<div className="w-full">
					<div className="h-[260px] sm:h-[300px] p-1.5 rounded-xl bg-light-background/50 dark:bg-black/20 border border-light-border dark:border-white/5">
						<GiftDynamicsScroll
							dynamics={dynamics}
							isLoading={isLoading}
							language={language}
						/>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
